<template>
  <div class="ai-analysis-layout">
    <!-- Sidebar -->
    <DemoSidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <DemoTopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <div class="title-section">
              <h1 class="page-title">AI Analysis</h1>
              <p class="page-subtitle">Comprehensive analysis of calls and WhatsApp conversations with AI-powered insights</p>
            </div>
          </div>
        </div>

        <!-- Unanalyzed WhatsApp Analysis Card -->
        <div class="whatsapp-analysis-row">
          <div class="stat-card whatsapp-analysis standalone">
            <div class="stat-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ unanalyzedStats.totalUnanalyzed || 0 }}</div>
              <div class="stat-label">Unanalyzed WhatsApp Conversations</div>
              <button
                @click="analyzeAllConversations"
                :disabled="loadingAnalyzeAll || unanalyzedStats.totalUnanalyzed === 0"
                class="analyze-all-btn"
              >
                <template v-if="loadingAnalyzeAll">
                  <span>Analyzing...</span>
                </template>
                <template v-else>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"/>
                  </svg>
                  <span>Analyze All</span>
                </template>
              </button>
            </div>
          </div>
        </div>

        <!-- Summary Statistics -->
        <div class="summary-stats">
          <div class="stat-card primary">
            <div class="stat-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ summaryStats.total_analyzed || 0 }}</div>
              <div class="stat-label">Total Analyzed</div>
              <div class="stat-trend positive">+15% this month</div>
            </div>
          </div>

          <div class="stat-card success">
            <div class="stat-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ summaryStats.satisfaction_rate || 0 }}%</div>
              <div class="stat-label">Satisfaction Rate</div>
              <div class="stat-trend positive">+8% from last week</div>
            </div>
          </div>

          <div class="stat-card warning">
            <div class="stat-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ summaryStats.resolution_rate || 0 }}%</div>
              <div class="stat-label">Resolution Rate</div>
              <div class="stat-trend negative">-2% needs attention</div>
            </div>
          </div>

          <div class="stat-card info">
            <div class="stat-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
              </svg>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ summaryStats.average_performance_score || 0 }}</div>
              <div class="stat-label">Avg Performance</div>
              <div class="stat-trend positive">+5.2 points improved</div>
            </div>
          </div>
        </div>

        <!-- Advanced Filters -->
        <div class="filters-section">
          <div class="filters-header">
            <h3>Advanced Filters</h3>
          </div>
          <div class="filters-grid">
            <div class="filter-group">
              <label class="filter-label">Analysis Type</label>
              <select v-model="filters.analysisType" @change="applyFilters" class="filter-select">
                <option value="">All Types</option>
                <option value="call">Phone Calls</option>
                <option value="whatsapp_conversation">WhatsApp Conversations</option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Agent</label>
              <select v-model="filters.agentId" @change="applyFilters" class="filter-select">
                <option value="">All Agents</option>
                <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                  {{ agent.name }}
                </option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Satisfaction Level</label>
              <select v-model="filters.satisfaction" @change="applyFilters" class="filter-select">
                <option value="">All Levels</option>
                <option value="satisfied">Satisfied</option>
                <option value="neutral">Neutral</option>
                <option value="dissatisfied">Dissatisfied</option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Resolution Status</label>
              <select v-model="filters.resolution" @change="applyFilters" class="filter-select">
                <option value="">All Status</option>
                <option value="resolved">Resolved</option>
                <option value="unresolved">Unresolved</option>
                <option value="escalated">Escalated</option>
                <option value="partial">Partial</option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">Date Range</label>
              <div class="date-range">
                <input
                  v-model="filters.dateFrom"
                  @change="applyFilters"
                  type="date"
                  class="filter-input"
                  placeholder="From"
                />
                <span class="date-separator">to</span>
                <input
                  v-model="filters.dateTo"
                  @change="applyFilters"
                  type="date"
                  class="filter-input"
                  placeholder="To"
                />
              </div>
            </div>

            <div class="filter-group">
              <label class="filter-label">Satisfaction Score</label>
              <div class="score-range">
                <input
                  v-model="filters.minSatisfactionScore"
                  @change="applyFilters"
                  type="number"
                  min="0"
                  max="100"
                  class="filter-input"
                  placeholder="Min"
                />
                <span class="score-separator">-</span>
                <input
                  v-model="filters.maxSatisfactionScore"
                  @change="applyFilters"
                  type="number"
                  min="0"
                  max="100"
                  class="filter-input"
                  placeholder="Max"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Analysis Results -->
        <div class="analysis-section">
          <div class="section-header">
            <h3>Analysis Results</h3>
            <div class="table-controls">
              <div class="search-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
                <input
                  v-model="searchTerm"
                  @input="searchAnalyses"
                  type="text"
                  placeholder="Search by customer name, phone, or topics..."
                  class="search-input"
                />
              </div>
            </div>
          </div>

          <div class="analysis-grid">
            <div v-for="analysis in analyses" :key="analysis.id" class="analysis-card" @click="viewAnalysis(analysis)">
              <div class="card-header">
                <div class="analysis-info">
                  <div class="analysis-id">#{{ analysis.id }}</div>
                  <div class="analysis-type">
                    <span :class="['type-badge', analysis.analysis_type]">
                      {{ analysis.analysis_type === 'call' ? 'Phone Call' : 'WhatsApp' }}
                    </span>
                  </div>
                </div>
                <div class="analysis-date">{{ formatDate(analysis.created_at) }}</div>
              </div>

              <div class="card-body">
                <div class="customer-info">
                  <div v-if="analysis.analysis_type === 'whatsapp_conversation' && analysis.customer_name" class="customer-name">
                    {{ analysis.customer_name }}
                  </div>
                  <div class="customer-phone">{{ analysis.customer_phone }}</div>
                  <div v-if="analysis.agent_name" class="agent-name">with {{ analysis.agent_name }}</div>
                  <div v-else class="no-agent">Self-service conversation</div>
                </div>

                <div class="metrics-overview">
                  <div class="metric-item">
                    <div class="metric-label">Satisfaction</div>
                    <div class="metric-value">
                      <div class="satisfaction-bar">
                        <div
                          class="satisfaction-fill"
                          :style="{ width: analysis.customer_satisfaction_score + '%' }"
                          :class="getSatisfactionClass(analysis.customer_satisfaction_prediction)"
                        ></div>
                      </div>
                      <span class="metric-number">{{ analysis.customer_satisfaction_score }}%</span>
                    </div>
                  </div>

                  <div class="metric-item">
                    <div class="metric-label">Performance</div>
                    <div class="metric-value">
                      <div class="performance-circle">
                        <svg viewBox="0 0 36 36" class="circular-chart">
                          <path
                            class="circle-bg"
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            class="circle"
                            :stroke-dasharray="`${analysis.agent_performance_score || 0}, 100`"
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                      </div>
                      <span class="metric-number">{{ analysis.agent_performance_score || 'N/A' }}</span>
                    </div>
                  </div>
                </div>

                <div class="sentiment-indicators">
                  <div class="sentiment-item positive">
                    <div class="sentiment-content">
                      <div class="sentiment-label">Positive</div>
                      <div class="sentiment-percentage">{{ analysis.overall_sentiment_positive }}%</div>
                    </div>
                    <div class="sentiment-bar">
                      <div class="sentiment-fill positive" :style="{ width: analysis.overall_sentiment_positive + '%' }"></div>
                    </div>
                  </div>
                  <div class="sentiment-item neutral">
                    <div class="sentiment-content">
                      <div class="sentiment-label">Neutral</div>
                      <div class="sentiment-percentage">{{ analysis.overall_sentiment_neutral }}%</div>
                    </div>
                    <div class="sentiment-bar">
                      <div class="sentiment-fill neutral" :style="{ width: analysis.overall_sentiment_neutral + '%' }"></div>
                    </div>
                  </div>
                  <div class="sentiment-item negative">
                    <div class="sentiment-content">
                      <div class="sentiment-label">Negative</div>
                      <div class="sentiment-percentage">{{ analysis.overall_sentiment_negative }}%</div>
                    </div>
                    <div class="sentiment-bar">
                      <div class="sentiment-fill negative" :style="{ width: analysis.overall_sentiment_negative + '%' }"></div>
                    </div>
                  </div>
                </div>

                <div class="key-insights">
                  <div class="insight-item">
                    <span class="insight-label">Resolution:</span>
                    <span :class="['resolution-status', analysis.issue_resolution_status]">
                      {{ formatResolution(analysis.issue_resolution_status) }}
                    </span>
                  </div>
                  <div class="insight-item">
                    <span class="insight-label">Escalation Risk:</span>
                    <span class="escalation-risk" :class="getEscalationRiskClass(analysis.escalation_risk_score)">
                      {{ analysis.escalation_risk_score }}%
                    </span>
                  </div>
                </div>

                <div v-if="analysis.key_topics && analysis.key_topics.length" class="topics-preview">
                  <div class="topics-label">Key Topics:</div>
                  <div class="topics-tags">
                    <span
                      v-for="(topic, index) in analysis.key_topics.slice(0, 3)"
                      :key="index"
                      class="topic-tag"
                    >
                      {{ topic }}
                    </span>
                    <span v-if="analysis.key_topics.length > 3" class="more-topics">
                      +{{ analysis.key_topics.length - 3 }} more
                    </span>
                  </div>
                </div>
              </div>

              <div class="card-footer">
                <div class="footer-left">
                  <div class="conversation-length">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
                    </svg>
                    {{ analysis.conversation_length }} messages
                  </div>
                  <div class="processing-info">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    {{ analysis.processing_time_seconds }}s processing
                  </div>
                </div>
                <div class="footer-right">
                  <CRMStatusIndicator
                    :status="crmStatus"
                    :showSyncButton="true"
                    :syncing="syncingAnalysis === analysis.id"
                    @sync="syncAnalysisToCRM(analysis)"
                    class="compact" />
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Analyzing conversations...</p>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && analyses.length === 0" class="empty-state">
            <div class="empty-icon">🤖</div>
            <h3>No analyses found</h3>
            <p>No conversation analyses match your current filters.</p>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.total_pages > 1" class="pagination">
            <button
              @click="goToPage(pagination.current_page - 1)"
              :disabled="!pagination.has_prev_page"
              class="pagination-btn"
            >
              Previous
            </button>

            <div class="pagination-info">
              Page {{ pagination.current_page }} of {{ pagination.total_pages }}
              ({{ pagination.total_count }} total analyses)
            </div>

            <button
              @click="goToPage(pagination.current_page + 1)"
              :disabled="!pagination.has_next_page"
              class="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>

        <!-- AI Analysis Detail Modal -->
        <AIAnalysisDetailModal
          :visible="showDetailModal"
          :analysis="selectedAnalysis"
          @close="showDetailModal = false"
        />
      </main>
    </div>
  </div>
</template>

<script>
import DemoSidebar from '../components/DemoSidebar.vue'
import DemoTopHeader from '../components/DemoTopHeader.vue'
import AIAnalysisDetailModal from '../components/AIAnalysisDetailModal.vue'
import CRMStatusIndicator from '../components/CRMStatusIndicator.vue'

export default {
  name: 'DemoAIAnalysisView',
  components: {
    DemoSidebar,
    DemoTopHeader,
    AIAnalysisDetailModal,
    CRMStatusIndicator
  },
  data () {
    return {
      analyses: [],
      allAnalyses: [], // Store all dummy data
      agents: [],
      pagination: {},
      summaryStats: {},
      unanalyzedStats: {},
      loading: false,
      loadingAnalyzeAll: false,
      searchTerm: '',
      filters: {
        analysisType: '',
        agentId: '',
        satisfaction: '',
        resolution: '',
        dateFrom: '',
        dateTo: '',
        minSatisfactionScore: '',
        maxSatisfactionScore: ''
      },
      showDetailModal: false,
      selectedAnalysis: null,
      crmStatus: {
        connected: false,
        sync_enabled: false,
        token_expired: false
      },
      syncingAnalysis: null
    }
  },
  mounted () {
    this.initializeDummyData()
    this.loadAnalyses()
    this.loadAgents()
    this.loadUnanalyzedCount()
    this.loadCRMStatus()
  },
  methods: {
    initializeDummyData () {
      // Initialize dummy agents
      this.agents = [
        { id: 1, name: 'Sarah Johnson' },
        { id: 2, name: 'Michael Chen' },
        { id: 3, name: 'Emily Rodriguez' },
        { id: 4, name: 'David Kim' },
        { id: 5, name: 'Jessica Martinez' },
        { id: 6, name: 'Robert Thompson' }
      ]

      // Initialize summary stats
      this.summaryStats = {
        total_analyzed: 247,
        satisfaction_rate: 87,
        resolution_rate: 92,
        average_performance_score: 8.4
      }

      // Initialize unanalyzed stats
      this.unanalyzedStats = {
        totalUnanalyzed: 12
      }

      // Customer names pool
      const customerNames = [
        'John Anderson', 'Maria Garcia', 'James Wilson', 'Patricia Brown',
        'Jennifer Davis', 'Robert Miller', 'Linda Martinez', 'Michael Taylor',
        'Elizabeth Thomas', 'William Jackson', 'Sarah White', 'David Harris',
        'Nancy Martin', 'Richard Thompson', 'Lisa Garcia', 'Joseph Lee',
        'Karen Rodriguez', 'Charles Lewis', 'Betty Walker', 'Daniel Hall',
        'Sandra Allen', 'Matthew Young', 'Ashley King', 'Christopher Wright'
      ]

      // Topics pool
      const topicsPools = [
        ['Product Inquiry', 'Pricing', 'Features'],
        ['Technical Support', 'Bug Report', 'Installation'],
        ['Billing', 'Payment Issue', 'Refund Request'],
        ['Account Setup', 'Password Reset', 'Login Issues'],
        ['Shipping', 'Delivery Time', 'Tracking'],
        ['Return Request', 'Product Quality', 'Exchange'],
        ['General Inquiry', 'Product Availability', 'Stock'],
        ['Upgrade Request', 'Plan Change', 'Subscription'],
        ['Complaint', 'Service Issue', 'Escalation'],
        ['Feedback', 'Suggestion', 'Feature Request']
      ]

      const resolutionStatuses = ['resolved', 'unresolved', 'escalated', 'partial']
      const analysisTypes = ['call', 'whatsapp_conversation']

      // Generate 25 realistic AI analysis records
      this.allAnalyses = Array.from({ length: 25 }, (_, i) => {
        const analysisType = analysisTypes[Math.floor(Math.random() * analysisTypes.length)]
        const hasAgent = Math.random() > 0.3
        const agent = hasAgent ? this.agents[Math.floor(Math.random() * this.agents.length)] : null

        // Generate realistic satisfaction score
        const satisfactionScore = Math.floor(Math.random() * 40) + 60 // 60-100
        let satisfactionPrediction = 'satisfied'
        if (satisfactionScore < 70) satisfactionPrediction = 'dissatisfied'
        else if (satisfactionScore < 85) satisfactionPrediction = 'neutral'

        // Generate realistic sentiment distribution
        const positiveBase = Math.floor(Math.random() * 30) + 50 // 50-80
        const negativeBase = Math.floor(Math.random() * 20) + 5 // 5-25
        const neutralBase = 100 - positiveBase - negativeBase

        // Generate performance score
        const performanceScore = hasAgent ? (Math.floor(Math.random() * 30) + 70) : null // 70-100

        // Generate escalation risk (inverse of satisfaction)
        const escalationRisk = Math.max(5, 100 - satisfactionScore + Math.floor(Math.random() * 20) - 10)

        // Generate resolution status
        let resolutionStatus = 'resolved'
        if (satisfactionScore < 70) {
          resolutionStatus = resolutionStatuses[Math.floor(Math.random() * resolutionStatuses.length)]
        } else if (satisfactionScore < 85) {
          resolutionStatus = Math.random() > 0.5 ? 'resolved' : 'partial'
        }

        // Select topics
        const topics = topicsPools[Math.floor(Math.random() * topicsPools.length)]

        // Generate date (within last 30 days)
        const daysAgo = Math.floor(Math.random() * 30)
        const date = new Date()
        date.setDate(date.getDate() - daysAgo)

        return {
          id: 1001 + i,
          analysis_type: analysisType,
          customer_name: analysisType === 'whatsapp_conversation' ? customerNames[i % customerNames.length] : null,
          customer_phone: `+1-555-${String(Math.floor(Math.random() * 9000) + 1000).padStart(4, '0')}`,
          agent_name: agent ? agent.name : null,
          agent_id: agent ? agent.id : null,
          customer_satisfaction_score: satisfactionScore,
          customer_satisfaction_prediction: satisfactionPrediction,
          agent_performance_score: performanceScore,
          overall_sentiment_positive: positiveBase,
          overall_sentiment_neutral: neutralBase,
          overall_sentiment_negative: negativeBase,
          issue_resolution_status: resolutionStatus,
          escalation_risk_score: escalationRisk,
          key_topics: topics,
          conversation_length: Math.floor(Math.random() * 40) + 10, // 10-50 messages
          processing_time_seconds: (Math.random() * 4 + 1).toFixed(1), // 1-5 seconds
          created_at: date.toISOString(),
          transcript: 'Demo transcript content',
          ai_summary: 'Demo AI summary of the conversation'
        }
      })

      // Sort by date descending
      this.allAnalyses.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },

    loadAnalyses (page = 1) {
      this.loading = true

      // Simulate API delay
      setTimeout(() => {
        let filtered = [...this.allAnalyses]

        // Apply filters
        if (this.filters.analysisType) {
          filtered = filtered.filter(a => a.analysis_type === this.filters.analysisType)
        }

        if (this.filters.agentId) {
          filtered = filtered.filter(a => a.agent_id === parseInt(this.filters.agentId))
        }

        if (this.filters.satisfaction) {
          filtered = filtered.filter(a => a.customer_satisfaction_prediction === this.filters.satisfaction)
        }

        if (this.filters.resolution) {
          filtered = filtered.filter(a => a.issue_resolution_status === this.filters.resolution)
        }

        if (this.filters.dateFrom) {
          const fromDate = new Date(this.filters.dateFrom)
          filtered = filtered.filter(a => new Date(a.created_at) >= fromDate)
        }

        if (this.filters.dateTo) {
          const toDate = new Date(this.filters.dateTo)
          toDate.setHours(23, 59, 59, 999)
          filtered = filtered.filter(a => new Date(a.created_at) <= toDate)
        }

        if (this.filters.minSatisfactionScore) {
          filtered = filtered.filter(a => a.customer_satisfaction_score >= parseInt(this.filters.minSatisfactionScore))
        }

        if (this.filters.maxSatisfactionScore) {
          filtered = filtered.filter(a => a.customer_satisfaction_score <= parseInt(this.filters.maxSatisfactionScore))
        }

        // Apply search
        if (this.searchTerm) {
          const term = this.searchTerm.toLowerCase()
          filtered = filtered.filter(a =>
            (a.customer_name && a.customer_name.toLowerCase().includes(term)) ||
            a.customer_phone.toLowerCase().includes(term) ||
            (a.agent_name && a.agent_name.toLowerCase().includes(term)) ||
            a.key_topics.some(topic => topic.toLowerCase().includes(term))
          )
        }

        // Pagination
        const perPage = 10
        const totalPages = Math.ceil(filtered.length / perPage)
        const start = (page - 1) * perPage
        const end = start + perPage

        this.analyses = filtered.slice(start, end)
        this.pagination = {
          current_page: page,
          total_pages: totalPages,
          total_count: filtered.length,
          has_prev_page: page > 1,
          has_next_page: page < totalPages
        }

        this.loading = false
      }, 300)
    },

    loadAgents () {
      // Already initialized in initializeDummyData
    },

    applyFilters () {
      this.loadAnalyses(1)
    },

    searchAnalyses () {
      // Debounce search
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadAnalyses(1)
      }, 500)
    },

    goToPage (page) {
      if (page >= 1 && page <= this.pagination.total_pages) {
        this.loadAnalyses(page)
      }
    },

    viewAnalysis (analysis) {
      this.selectedAnalysis = analysis
      this.showDetailModal = true
    },

    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString()
    },

    formatResolution (status) {
      const statusMap = {
        resolved: 'Resolved',
        unresolved: 'Unresolved',
        escalated: 'Escalated',
        partial: 'Partial'
      }
      return statusMap[status] || status
    },

    getSatisfactionClass (prediction) {
      const classMap = {
        satisfied: 'satisfied',
        neutral: 'neutral',
        dissatisfied: 'dissatisfied'
      }
      return classMap[prediction] || 'neutral'
    },

    getEscalationRiskClass (score) {
      if (score >= 70) return 'high'
      if (score >= 40) return 'medium'
      return 'low'
    },

    loadUnanalyzedCount () {
      // Already initialized in initializeDummyData
    },

    analyzeAllConversations () {
      if (this.loadingAnalyzeAll || this.unanalyzedStats.totalUnanalyzed === 0) return

      this.loadingAnalyzeAll = true

      // Simulate analysis process
      setTimeout(() => {
        this.showNotification('success', 'Analysis Started', `Analyzing ${this.unanalyzedStats.totalUnanalyzed} conversations...`)

        // Simulate completion
        setTimeout(() => {
          this.unanalyzedStats.totalUnanalyzed = 0
          this.summaryStats.total_analyzed += 12
          this.loadingAnalyzeAll = false
          this.showNotification('success', 'Analysis Complete', 'All conversations have been analyzed successfully!')
        }, 2000)
      }, 500)
    },

    showNotification (type, title, message) {
      // Simple notification - you could integrate with your existing notification system
      console.log(`${type.toUpperCase()}: ${title} - ${message}`)

      // Show browser notification for now
      if (type === 'success') {
        alert(`${title}: ${message}`)
      } else {
        alert(`${title}: ${message}`)
      }
    },

    loadCRMStatus () {
      // Dummy CRM status
      this.crmStatus = {
        connected: false,
        sync_enabled: false,
        token_expired: false
      }
    },

    syncAnalysisToCRM (analysis) {
      if (!this.crmStatus.connected || this.crmStatus.token_expired || !this.crmStatus.sync_enabled) {
        this.showNotification('error', 'CRM Sync Failed', 'CRM is not connected or sync is disabled')
        return
      }

      this.syncingAnalysis = analysis.id

      // Simulate sync
      setTimeout(() => {
        this.showNotification('success', 'CRM Sync Success', `Analysis #${analysis.id} synced to CRM`)
        this.syncingAnalysis = null
      }, 1500)
    }
  }
}
</script>

<style scoped>
.ai-analysis-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.page-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
}

.title-section h1 {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

/* WhatsApp Analysis Row */
.whatsapp-analysis-row {
  margin-bottom: 32px;
  display: flex;
  justify-content: flex-end;
}

.stat-card.standalone {
  max-width: 500px;
  width: 100%;
}

/* Summary Statistics */
.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 28px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--accent-color), var(--accent-color-secondary));
}

.stat-card.primary::before { background: linear-gradient(90deg, #667eea, #764ba2); }
.stat-card.success::before { background: linear-gradient(90deg, #10b981, #059669); }
.stat-card.warning::before { background: linear-gradient(90deg, #f59e0b, #d97706); }
.stat-card.info::before { background: linear-gradient(90deg, #3b82f6, #2563eb); }
.stat-card.whatsapp-analysis::before { background: linear-gradient(90deg, #25D366, #128C7E); }

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px var(--shadow-primary);
  border-color: var(--border-secondary);
}

.stat-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
}

.stat-card.primary .stat-icon { background: linear-gradient(135deg, #667eea, #764ba2); }
.stat-card.success .stat-icon { background: linear-gradient(135deg, #10b981, #059669); }
.stat-card.warning .stat-icon { background: linear-gradient(135deg, #f59e0b, #d97706); }
.stat-card.info .stat-icon { background: linear-gradient(135deg, #3b82f6, #2563eb); }
.stat-card.whatsapp-analysis .stat-icon { background: linear-gradient(135deg, #25D366, #128C7E); }

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 32px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  font-weight: 500;
}

.stat-trend {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 12px;
  display: inline-block;
}

.stat-trend.positive {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.stat-trend.negative {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

/* Filters Section */
.filters-section {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  padding: 28px;
  margin-bottom: 32px;
}

.filters-header {
  margin-bottom: 24px;
}

.filters-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.filter-select,
.filter-input {
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.date-range,
.score-range {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-separator,
.score-separator {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Analysis Section */
.analysis-section {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px;
  border-bottom: 1px solid var(--border-primary);
}

.section-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box svg {
  position: absolute;
  left: 16px;
  color: var(--text-secondary);
}

.search-input {
  padding: 12px 16px 12px 48px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  width: 320px;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Analysis Grid */
.analysis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(480px, 1fr));
  gap: 24px;
  padding: 24px;
}

.analysis-card {
  background: var(--bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.analysis-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.analysis-card:hover::before {
  transform: scaleX(1);
}

.analysis-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  border-color: var(--border-secondary);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-primary);
}

.analysis-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.analysis-id {
  font-family: monospace;
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 14px;
}

.type-badge {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.type-badge.call {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.type-badge.whatsapp_conversation {
  background: linear-gradient(135deg, #10b981, #059669);
}

.analysis-date {
  font-size: 13px;
  color: var(--text-secondary);
}

.card-body {
  margin-bottom: 24px;
}

.customer-info {
  margin-bottom: 18px;
  padding: 14px 16px;
  background: var(--bg-primary);
  border-radius: 10px;
  border: 1px solid var(--border-primary);
}

.customer-name {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 20px;
  margin-bottom: 4px;
}

.customer-phone {
  font-family: monospace;
  font-weight: 700;
  color: var(--text-primary);
  font-size: 18px;
}

.agent-name {
  font-size: 15px;
  color: var(--text-secondary);
  margin-top: 6px;
  font-weight: 500;
}

.no-agent {
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
  margin-top: 4px;
}

/* Metrics Overview */
.metrics-overview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 18px;
  padding: 16px;
  background: var(--bg-primary);
  border-radius: 10px;
  border: 1px solid var(--border-primary);
}

.metric-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metric-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  display: flex;
  align-items: center;
  gap: 12px;
}

.satisfaction-bar {
  flex: 1;
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  overflow: hidden;
}

.satisfaction-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.satisfaction-fill.satisfied {
  background: linear-gradient(90deg, #10b981, #059669);
}

.satisfaction-fill.neutral {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.satisfaction-fill.dissatisfied {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.performance-circle {
  width: 32px;
  height: 32px;
}

.circular-chart {
  display: block;
  margin: 0 auto;
  max-width: 100%;
  max-height: 100%;
}

.circle-bg {
  fill: none;
  stroke: var(--bg-tertiary);
  stroke-width: 2.8;
}

.circle {
  fill: none;
  stroke: #667eea;
  stroke-width: 2.8;
  stroke-linecap: round;
  animation: progress 1s ease-out forwards;
}

.metric-number {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  min-width: 50px;
  text-align: right;
}

/* Sentiment Indicators */
.sentiment-indicators {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
  padding: 16px;
  background: var(--bg-primary);
  border-radius: 10px;
  border: 1px solid var(--border-primary);
}

.sentiment-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.sentiment-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 80px;
}

.sentiment-label {
  font-size: 13px;
  font-weight: 600;
  text-transform: capitalize;
}

.sentiment-percentage {
  font-size: 18px;
  font-weight: 700;
}

.sentiment-bar {
  flex: 1;
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  overflow: hidden;
}

.sentiment-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.sentiment-fill.positive {
  background: linear-gradient(90deg, #10b981, #059669);
}

.sentiment-fill.neutral {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.sentiment-fill.negative {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.sentiment-item.positive .sentiment-label,
.sentiment-item.positive .sentiment-percentage {
  color: #059669;
}

.sentiment-item.neutral .sentiment-label,
.sentiment-item.neutral .sentiment-percentage {
  color: #d97706;
}

.sentiment-item.negative .sentiment-label,
.sentiment-item.negative .sentiment-percentage {
  color: #dc2626;
}

/* Key Insights */
.key-insights {
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 14px 16px;
  background: var(--bg-primary);
  border-radius: 10px;
  border: 1px solid var(--border-primary);
}

.insight-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
}

.insight-label {
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 13px;
}

.resolution-status {
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
}

.resolution-status.resolved {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.resolution-status.escalated {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.resolution-status.pending {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.escalation-risk {
  font-weight: 700;
  font-size: 14px;
}

.escalation-risk.low { color: #059669; }
.escalation-risk.medium { color: #d97706; }
.escalation-risk.high { color: #dc2626; }

/* Topics Preview */
.topics-preview {
  margin-bottom: 12px;
  padding: 14px 16px;
  background: var(--bg-primary);
  border-radius: 10px;
  border: 1px solid var(--border-primary);
}

.topics-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 10px;
}

.topics-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.topic-tag {
  padding: 6px 12px;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.more-topics {
  padding: 6px 12px;
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

/* Card Footer */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  margin-top: 12px;
  border-top: 1px solid var(--border-primary);
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--bg-primary);
  border-radius: 10px;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.footer-right {
  display: flex;
  align-items: center;
}

.conversation-length,
.processing-info {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 80px;
  color: var(--text-secondary);
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary);
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.empty-icon {
  font-size: 56px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-top: 1px solid var(--border-primary);
}

.pagination-btn {
  padding: 10px 20px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.pagination-info {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes progress {
  0% { stroke-dasharray: 0 100; }
}

/* Analyze All Button */
.analyze-all-btn {
  margin-top: 16px;
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #25D366, #128C7E);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.analyze-all-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #128C7E, #075E54);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(37, 211, 102, 0.4);
}

.analyze-all-btn:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.analyze-all-btn:disabled:hover {
  background: #e5e7eb;
  transform: none;
  box-shadow: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .summary-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .analysis-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 20px;
  }

  .search-input {
    width: 100%;
  }

  .metrics-overview {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .key-insights {
    flex-direction: column;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .summary-stats {
    grid-template-columns: 1fr;
  }

  .stat-card {
    padding: 20px;
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }

  .analysis-card {
    padding: 16px;
  }

  .pagination {
    flex-direction: column;
    gap: 16px;
  }
}
</style>
